import axios from 'axios';
import { API_CONFIG } from './config/api';

const apiClient = axios.create({
  baseURL: API_CONFIG.baseURL,
});

// Master-Detail Property Types
export interface ImmobilienProjekt {
  id: number;
  dokumentId?: string;
  projektname: string;
  projekt_slug: string;
  objektnummer: string;
  projektstatus: 'aktueller_verkauf' | 'zukuenftige_projekte' | 'vergangene_projekte';
  projekttyp: 'einzelobjekt' | 'mehrfamilienhaus' | 'wohnanlage' | 'gewerbekomplex' | 'mixed_use';
  vermarktungsart: 'kauf' | 'miete' | 'kauf_und_miete';
  
  basis_adresse: string;
  stadt: string;
  postleitzahl: string;
  stadtteil?: string;
  bundesland: string;
  koordinaten_lat?: number;
  koordinaten_lng?: number;
  
  fertigstellung?: string;
  baubeginn?: string;
  bautraeger?: string;
  architekt?: string;
  baujahr?: number;
  gesamteinheiten: number;
  verkaufte_einheiten: number;
  
  basis_ausstattung: string[];
  energiestandard?: string;
  heizungsart?: string;
  
  projekt_bilder?: Array<{
    id: number;
    attributes: {
      url: string;
      formats?: {
        thumbnail?: { url: string };
        small?: { url: string };
        medium?: { url: string };
        large?: { url: string };
      };
    };
  }>;
  grundrissplaene?: any[];
  lageplaene?: any[];
  exposee?: any;
  
  vermarktung_aktiv: boolean;
  prioritaet: number;
  hervorgehoben: boolean;
  seo_titel?: string;
  seo_beschreibung?: string;
  
  ansprechpartner?: string;
  kontakt_email?: string;
  kontakt_telefon?: string;
  
  beschreibung?: any;
  kurze_beschreibung?: string;
  highlights: string[];
  lage_beschreibung?: string;
  ausstattung_beschreibung?: string;
  virtual_tour_link?: string;
  
  automatische_verschiebung: boolean;
  verkauft_am?: string;
  
  immobilien_einheiten?: ImmobilienEinheit[];
  einheiten_count?: number;
  
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface ImmobilienEinheit {
  id: number;
  dokumentId?: string;
  projekt?: ImmobilienProjekt | number;
  
  titel: string;
  einheit_slug: string;
  objektnummer: string;
  einheit_nummer?: string;
  
  beschreibung?: any;
  
  kaufpreis?: number;
  mietpreis?: number;
  nebenkosten?: number;
  hausgeld?: number;
  kaution?: number;
  provision: number;
  preis_pro_qm?: number;
  vermarktungsart: 'kauf' | 'miete' | 'kauf_und_miete';
  
  wohnflaeche: number;
  nutzflaeche?: number;
  grundstuecksflaeche?: number;
  zimmeranzahl: number;
  schlafzimmer: number;
  badezimmer: number;
  gaeste_wc: boolean;
  
  stadt: string;
  stadtteil?: string;
  adresse?: string;
  postleitzahl: string;
  bundesland: string;
  koordinaten_lat?: number;
  koordinaten_lng?: number;
  
  etage?: number;
  lage_im_haus?: string;
  balkon_terrasse: boolean;
  balkon_terrasse_flaeche?: number;
  garten: boolean;
  garten_flaeche?: number;
  
  immobilientyp: string;
  
  baujahr?: number;
  renoviert?: number;
  energieausweis?: string;
  energieverbrauch?: number;
  heizungsart?: string;
  
  ausstattung: string[];
  zusatz_ausstattung: string[];
  ausstattung_abweichungen: string[];
  
  keller: boolean;
  kellerflaeche?: number;
  dachboden: boolean;
  aufzug: boolean;
  rollstuhlgerecht: boolean;
  einbaukueche: boolean;
  moebel_uebernahme: boolean;
  haustiere_erlaubt: boolean;
  
  stellplatz_typ: string;
  stellplaetze_anzahl: number;
  stellplatz_preis?: number;
  
  verfuegbarkeitsstatus: 'verfuegbar' | 'reserviert' | 'verkauft' | 'vermietet' | 'rueckabwicklung';
  verfuegbar_ab?: string;
  verkauft_am?: string;
  vermietet_am?: string;
  
  bilder?: Array<{
    id: number;
    attributes: {
      url: string;
      formats?: {
        thumbnail?: { url: string };
        small?: { url: string };
        medium?: { url: string };
        large?: { url: string };
      };
    };
  }>;
  grundriss?: any;
  exposee?: any;
  video_tour?: string;
  
  seo_titel?: string;
  seo_beschreibung?: string;
  keywords: string[];
  
  ansprechpartner?: string;
  besichtigungstermine: any[];
  notizen?: string;
  
  prioritaet: number;
  hervorgehoben: boolean;
  sichtbar_auf_website: boolean;
  
  automatische_verschiebung: boolean;
  
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

// Legacy Property interface for backward compatibility
export interface Property extends ImmobilienEinheit {
  // Map old field names to new ones
  Title?: string; // maps to titel
  description?: string; // maps to beschreibung
  price?: number; // maps to kaufpreis
  priceUnit?: string; // deprecated
  area?: number; // maps to wohnflaeche
  rooms?: number; // maps to zimmeranzahl
  bedrooms?: number; // maps to schlafzimmer
  bathrooms?: number; // maps to badezimmer
  city?: string; // maps to stadt
  district?: string; // maps to stadtteil
  address?: string; // maps to adresse
  PLZ?: number; // maps to postleitzahl
  latitude?: string; // maps to koordinaten_lat
  longitude?: string; // maps to koordinaten_lng
  propertytype?: string; // maps to immobilientyp
  Baujahr?: number; // maps to baujahr
  features?: string[]; // maps to ausstattung
  images?: any[]; // maps to bilder
  LaunchDate?: string; // maps to verfuegbar_ab
  ObjectID?: string; // maps to objektnummer
  Immobilientyp?: string; // maps to immobilientyp
}

// Type definitions for blog (unchanged)
export interface Article {
  id: number;
  attributes?: {
    // Using camelCase for consistency in our code
    titel: string;
    slug: string;
    inhalt: any; // Rich text content
    zusammenfassung: string;
    titelbild: {
      data: {
        attributes: {
          url: string;
          formats: {
            thumbnail: { url: string };
            small: { url: string };
            medium: { url: string };
          };
        };
      };
    };
    autor: string;
    veröffentlichungsdatum: string;
    kategorie: string;
    hervorgehoben: boolean;
    createdAt: string;
    updatedAt: string;
  };
  // Direct access to capitalized properties from Strapi
  Titel?: string;
  Slug?: string;
  Inhalt?: any;
  Zusammenfassung?: string;
  Autor?: string;
  Veroeffentlichungsdatum?: string;
  Kategorie?: string;
  Hervorgehoben?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ArticleResponse {
  data: Article[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface SingleArticleResponse {
  data: Article;
}

// Helper function to convert frontend filters to Strapi format
function buildStrapiFilters(filters: Record<string, any> = {}) {
  const strapiFilters: Record<string, any> = {};
  
  // Map our frontend filters to Strapi filter syntax
  if (filters.city) strapiFilters['filters[city][$eq]'] = filters.city;
  if (filters.propertyType) strapiFilters['filters[propertytype][$eq]'] = filters.propertyType;
  
  // Handle price range
  if (filters.minPrice) strapiFilters['filters[price][$gte]'] = filters.minPrice;
  if (filters.maxPrice) strapiFilters['filters[price][$lte]'] = filters.maxPrice;
  
  // Handle rooms and area
  if (filters.minRooms) strapiFilters['filters[rooms][$gte]'] = filters.minRooms;
  if (filters.minArea) strapiFilters['filters[area][$gte]'] = filters.minArea;
  
  // Handle year built
  if (filters.yearBuilt) strapiFilters['filters[yearBuilt][$gte]'] = filters.yearBuilt;
  
  // Handle features (array of features)
  if (filters.features) {
    // For simplicity, we'll check if any feature matches
    // This needs to be adjusted based on how features are stored in Strapi
    strapiFilters['filters[features][$contains]'] = filters.features;
  }
  
  // Handle pagination
  if (filters.limit) strapiFilters['pagination[limit]'] = filters.limit;
  if (filters.page) strapiFilters['pagination[page]'] = filters.page;
  if (filters.sort) strapiFilters['sort'] = filters.sort;
  
  return strapiFilters;
}


// Blog related API functions
export const fetchArticles = async (params = {}) => {
  try {
    // Note: In Strapi v4, the collection name is pluralized
    const response = await apiClient.get('/api/artikels', {
      params: {
        populate: 'Titelbild',
        ...params
      },
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    // For debugging
    console.log('Articles response:', response.data);
    
    // Map the response to match our expected format if needed
    const mappedData = response.data.data.map((article: any) => ({
      id: article.id,
      attributes: {
        titel: article.Titel,
        slug: article.Slug,
        inhalt: article.Inhalt,
        zusammenfassung: article.Zusammenfassung,
        autor: article.Autor,
        veröffentlichungsdatum: article.Veroeffentlichungsdatum,
        kategorie: article.Kategorie,
        hervorgehoben: article.Hervorgehoben,
        createdAt: article.createdAt,
        updatedAt: article.updatedAt,
        // Preserve the nested structure of the image data
        titelbild: article.Titelbild
      }
    }));
    
    return {
      data: mappedData,
      meta: response.data.meta
    };
  } catch (error) {
    console.error('Error fetching articles:', error);
    // Return empty data structure to prevent UI errors
    return { data: [], meta: { pagination: { page: 1, pageSize: 10, pageCount: 0, total: 0 } } };
  }
};

export const fetchArticleBySlug = async (slug: string) => {
  try {
    const response = await apiClient.get('/api/artikels', {
      params: {
        'filters[Slug][$eq]': slug,
        'populate': 'Titelbild'
      },
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    // If article found, return the first item with mapped attributes
    if (response.data.data && response.data.data.length > 0) {
      const article = response.data.data[0];
      
      // Map to our expected format
      const mappedArticle = {
        id: article.id,
        attributes: {
          titel: article.Titel,
          slug: article.Slug,
          inhalt: article.Inhalt,
          zusammenfassung: article.Zusammenfassung,
          autor: article.Autor,
          veröffentlichungsdatum: article.Veroeffentlichungsdatum,
          kategorie: article.Kategorie,
          hervorgehoben: article.Hervorgehoben,
          createdAt: article.createdAt,
          updatedAt: article.updatedAt,
          // Preserve the nested structure of the image data
          titelbild: article.Titelbild
        }
      };
      
      return { data: mappedArticle };
    }
    
    throw new Error('Article not found');
  } catch (error) {
    console.error(`Error fetching article with slug ${slug}:`, error);
    throw error;
  }
};

export const fetchFeaturedArticles = async () => {
  try {
    const response = await apiClient.get('/api/artikels', {
      params: {
        'filters[Hervorgehoben][$eq]': true,
        'pagination[limit]': 3,
        'populate': 'Titelbild'
      },
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    // Map the response to match our expected format
    const mappedData = response.data.data.map((article: any) => ({
      id: article.id,
      attributes: {
        titel: article.Titel,
        slug: article.Slug,
        inhalt: article.Inhalt,
        zusammenfassung: article.Zusammenfassung,
        autor: article.Autor,
        veröffentlichungsdatum: article.Veroeffentlichungsdatum,
        kategorie: article.Kategorie,
        hervorgehoben: article.Hervorgehoben,
        createdAt: article.createdAt,
        updatedAt: article.updatedAt,
        // Preserve the nested structure of the image data
        titelbild: article.Titelbild
      }
    }));
    
    return {
      data: mappedData,
      meta: response.data.meta
    };
  } catch (error) {
    console.error('Error fetching featured articles:', error);
    return { data: [], meta: { pagination: { page: 1, pageSize: 3, pageCount: 0, total: 0 } } };
  }
};

export const fetchRelatedArticles = async (article: Article, limit = 3) => {
  try {
    // Get articles in the same category excluding the current article
    const kategorie = article.attributes?.kategorie || article.Kategorie;
    
    const response = await apiClient.get('/api/artikels', {
      params: {
        'filters[Kategorie][$eq]': kategorie,
        'filters[id][$ne]': article.id,
        'pagination[limit]': limit,
        'populate': 'Titelbild'
      },
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    // Map the response to match our expected format
    const mappedData = response.data.data.map((article: any) => ({
      id: article.id,
      attributes: {
        titel: article.Titel,
        slug: article.Slug,
        inhalt: article.Inhalt,
        zusammenfassung: article.Zusammenfassung,
        autor: article.Autor,
        veröffentlichungsdatum: article.Veroeffentlichungsdatum,
        kategorie: article.Kategorie,
        hervorgehoben: article.Hervorgehoben,
        createdAt: article.createdAt,
        updatedAt: article.updatedAt,
        // Preserve the nested structure of the image data
        titelbild: article.Titelbild
      }
    }));
    
    return {
      data: mappedData,
      meta: response.data.meta
    };
  } catch (error) {
    console.error('Error fetching related articles:', error);
    return { data: [], meta: { pagination: { page: 1, pageSize: limit, pageCount: 0, total: 0 } } };
  }
};

// Master-Detail API Functions

// Immobilien-Projekte API
export const fetchImmobilienProjekte = async (filters?: Record<string, any>) => {
  try {
    const params: any = {
      populate: {
        projekt_bilder: true,
        immobilien_einheiten: {
          populate: ['bilder']
        }
      }
    };

    // Add filters for kategorien
    if (filters?.status) {
      params['filters[projektstatus][$eq]'] = filters.status;
    }
    if (filters?.vermarktung_aktiv !== undefined) {
      params['filters[vermarktung_aktiv][$eq]'] = filters.vermarktung_aktiv;
    }
    if (filters?.hervorgehoben) {
      params['filters[hervorgehoben][$eq]'] = true;
    }

    // Search filters
    if (filters?.stadt) params['filters[stadt][$eq]'] = filters.stadt;
    if (filters?.stadtteil) params['filters[stadtteil][$containsi]'] = filters.stadtteil;
    if (filters?.projekttyp) params['filters[projekttyp][$eq]'] = filters.projekttyp;
    if (filters?.vermarktungsart) params['filters[vermarktungsart][$eq]'] = filters.vermarktungsart;
    if (filters?.energiestandard) params['filters[energiestandard][$eq]'] = filters.energiestandard;
    if (filters?.bautraeger) params['filters[bautraeger][$containsi]'] = filters.bautraeger;
    
    // Unit count filtering
    if (filters?.minEinheiten) params['filters[gesamteinheiten][$gte]'] = filters.minEinheiten;
    if (filters?.maxEinheiten) params['filters[gesamteinheiten][$lte]'] = filters.maxEinheiten;
    
    // Date filtering for completion
    if (filters?.fertigstellungJahr) {
      const year = parseInt(filters.fertigstellungJahr);
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;
      params['filters[fertigstellung][$gte]'] = startDate;
      params['filters[fertigstellung][$lte]'] = endDate;
    }
    
    // Equipment/features filtering
    if (filters?.ausstattung) {
      params['filters[basis_ausstattung][$containsi]'] = filters.ausstattung;
    }

    // Pagination
    if (filters?.limit) params['pagination[limit]'] = filters.limit;
    if (filters?.page) params['pagination[page]'] = filters.page;

    // Sorting
    const sortMap: Record<string, string> = {
      'vergangene_projekte': 'verkauft_am:desc',
      'newest': 'createdAt:desc',
      'priority': 'prioritaet:desc',
      'fertigstellung': 'fertigstellung:asc'
    };
    params.sort = sortMap[filters?.sort || 'newest'] || 'createdAt:desc';

    const response = await apiClient.get('/api/immobilien-projekte', { params });
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching immobilien-projekte:', error);
    return [];
  }
};

export const fetchProjektBySlug = async (slug: string): Promise<ImmobilienProjekt | null> => {
  try {
    const response = await apiClient.get('/api/immobilien-projekte', {
      params: {
        'filters[projekt_slug][$eq]': slug,
        populate: {
          projekt_bilder: true,
          grundrissplaene: true,
          lageplaene: true,
          exposee: true,
          immobilien_einheiten: {
            populate: ['bilder', 'grundriss', 'exposee']
          }
        }
      }
    });

    return response.data.data?.[0] || null;
  } catch (error) {
    console.error('Error fetching projekt by slug:', error);
    return null;
  }
};

export const fetchProjektWithEinheiten = async (projektId: number) => {
  try {
    const response = await apiClient.get(`/api/immobilien-projekte/${projektId}/einheiten`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching projekt with einheiten:', error);
    return null;
  }
};

export const createBulkEinheiten = async (projektId: number, einheiten: Partial<ImmobilienEinheit>[]) => {
  try {
    const response = await apiClient.post(`/api/immobilien-projekte/${projektId}/bulk-einheiten`, {
      einheiten
    });
    return response.data.data;
  } catch (error) {
    console.error('Error creating bulk einheiten:', error);
    throw error;
  }
};

export const updateProjektStatus = async (projektId: number, status: string) => {
  try {
    const response = await apiClient.put(`/api/immobilien-projekte/${projektId}/status`, {
      status
    });
    return response.data.data;
  } catch (error) {
    console.error('Error updating projekt status:', error);
    throw error;
  }
};

// Immobilien-Einheiten API
export const fetchImmobilienEinheiten = async (filters?: Record<string, any>) => {
  try {
    const params: any = {
      populate: {
        bilder: true,
        grundriss: true,
        projekt: {
          populate: ['projekt_bilder']
        }
      }
    };

    // German field mapping for filters
    if (filters?.stadt) params['filters[stadt][$eq]'] = filters.stadt;
    if (filters?.immobilientyp) params['filters[immobilientyp][$eq]'] = filters.immobilientyp;
    if (filters?.verfuegbarkeitsstatus) params['filters[verfuegbarkeitsstatus][$eq]'] = filters.verfuegbarkeitsstatus;
    
    // Price filtering
    if (filters?.minKaufpreis) params['filters[kaufpreis][$gte]'] = filters.minKaufpreis;
    if (filters?.maxKaufpreis) params['filters[kaufpreis][$lte]'] = filters.maxKaufpreis;
    if (filters?.minMietpreis) params['filters[mietpreis][$gte]'] = filters.minMietpreis;
    if (filters?.maxMietpreis) params['filters[mietpreis][$lte]'] = filters.maxMietpreis;
    
    // Area and rooms
    if (filters?.minWohnflaeche) params['filters[wohnflaeche][$gte]'] = filters.minWohnflaeche;
    if (filters?.minZimmeranzahl) params['filters[zimmeranzahl][$gte]'] = filters.minZimmeranzahl;
    
    // Features
    if (filters?.ausstattung?.length > 0) {
      params['filters[ausstattung][$containsi]'] = filters.ausstattung.join(',');
    }
    
    // Only show visible properties
    if (filters?.sichtbar_auf_website !== false) {
      params['filters[sichtbar_auf_website][$eq]'] = true;
    }

    // Pagination
    if (filters?.limit) params['pagination[limit]'] = filters.limit;
    if (filters?.page) params['pagination[page]'] = filters.page;

    // Sorting
    const sortOptions: Record<string, string> = {
      'price_asc': 'kaufpreis:asc',
      'price_desc': 'kaufpreis:desc',
      'area_asc': 'wohnflaeche:asc', 
      'area_desc': 'wohnflaeche:desc',
      'newest': 'createdAt:desc',
      'priority': 'prioritaet:desc'
    };
    params.sort = sortOptions[filters?.sort || 'newest'] || 'createdAt:desc';

    const response = await apiClient.get('/api/properties', { params });
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching immobilien-einheiten:', error);
    return [];
  }
};

export const fetchEinheitBySlug = async (slug: string): Promise<ImmobilienEinheit | null> => {
  try {
    const response = await apiClient.get('/api/properties', {
      params: {
        'filters[einheit_slug][$eq]': slug,
        populate: {
          bilder: true,
          grundriss: true,
          exposee: true,
          projekt: {
            populate: ['projekt_bilder', 'grundrissplaene', 'lageplaene']
          }
        }
      }
    });

    return response.data.data?.[0] || null;
  } catch (error) {
    console.error('Error fetching einheit by slug:', error);
    return null;
  }
};

// Category-based fetching (for navigation)
export const fetchPropertiesByCategory = async (category: 'verkaufen' | 'zukuenftige_projekte' | 'vergangene_projekte', options?: any) => {
  const statusMap = {
    'verkaufen': 'aktueller_verkauf',
    'zukuenftige_projekte': 'zukuenftige_projekte', 
    'vergangene_projekte': 'vergangene_projekte'
  };

  // First, get projects in this category
  const projekte = await fetchImmobilienProjekte({
    status: statusMap[category],
    vermarktung_aktiv: true,
    ...(options || {})
  });

  // Then get all einheiten for these projects
  const einheiten = await fetchImmobilienEinheiten({
    verfuegbarkeitsstatus: category === 'vergangene_projekte' ? ['verkauft', 'vermietet'] : ['verfuegbar', 'reserviert'],
    sichtbar_auf_website: true,
    ...(options || {})
  });

  return {
    projekte,
    einheiten,
    totalCount: (projekte?.length || 0) + (einheiten?.length || 0)
  };
};

// Legacy API functions (updated to work with new schema)
export const fetchProperties = async (filters?: Record<string, any>): Promise<Property[]> => {
  // Map legacy filters to new German field names
  const mappedFilters: Record<string, any> = {};
  
  if (filters) {
    if (filters.city) mappedFilters.stadt = filters.city;
    if (filters.propertyType) mappedFilters.immobilientyp = filters.propertyType;
    if (filters.minPrice) mappedFilters.minKaufpreis = filters.minPrice;
    if (filters.maxPrice) mappedFilters.maxKaufpreis = filters.maxPrice;
    if (filters.minRooms) mappedFilters.minZimmeranzahl = filters.minRooms;
    if (filters.minArea) mappedFilters.minWohnflaeche = filters.minArea;
    if (filters.yearBuilt) mappedFilters.baujahr = filters.yearBuilt;
    if (filters.features) mappedFilters.ausstattung = filters.features;
  }

  const einheiten = await fetchImmobilienEinheiten(mappedFilters);
  
  // Map back to legacy Property interface
  return einheiten.map(mapEinheitToLegacyProperty);
};

export const fetchFeaturedProperties = async (): Promise<Property[]> => {
  const einheiten = await fetchImmobilienEinheiten({
    hervorgehoben: true,
    limit: 6
  });
  
  return einheiten.map(mapEinheitToLegacyProperty);
};

// Helper function to map new ImmobilienEinheit to legacy Property interface
function mapEinheitToLegacyProperty(einheit: ImmobilienEinheit): Property {
  return {
    ...einheit,
    // Legacy mappings
    Title: einheit.titel,
    description: einheit.beschreibung,
    price: einheit.kaufpreis,
    area: einheit.wohnflaeche,
    rooms: einheit.zimmeranzahl,
    bedrooms: einheit.schlafzimmer,
    bathrooms: einheit.badezimmer,
    city: einheit.stadt,
    district: einheit.stadtteil,
    address: einheit.adresse,
    PLZ: parseInt(einheit.postleitzahl),
    latitude: einheit.koordinaten_lat?.toString(),
    longitude: einheit.koordinaten_lng?.toString(),
    propertytype: einheit.immobilientyp,
    Baujahr: einheit.baujahr,
    features: einheit.ausstattung,
    images: einheit.bilder,
    LaunchDate: einheit.verfuegbar_ab,
    ObjectID: einheit.objektnummer,
    Immobilientyp: einheit.immobilientyp
  };
}

export const subscribeToNewsletter = async (email: string, name: string) => {
  try {
    const response = await apiClient.post('/api/newsletter-abonnents', {
      data: {
        email,
        name,
        anmeldedatum: new Date().toISOString(),
        aktiv: true
      }
    }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error subscribing to newsletter:', error);
    throw error;
  }
};

export default apiClient;
